import { Transition } from "@headlessui/react";
import {
  BellAlertIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { storyblokEditable } from "@storyblok/react/rsc";
import Link from "next/link";
import type { MouseEvent } from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import type { BannerItemStoryblok } from "@/component-types-sb";
import { CampsiteContext } from "@/lib/context/CampsiteContext";
import { getCookie, setCookie } from "@/lib/hooks/useCookie";
import { isBannerEligible } from "@/lib/storyblok/banner";
import { setProperty } from "@/lib/utils/styles";

type BannerItemPops = {
  blok: BannerItemStoryblok;
  onDismiss?: (reason: BannerDismissReason) => void;
  navigation?: {
    index: number;
    count: number;
    onPrevious: () => void;
    onNext: () => void;
  };
};

export type BannerDismissReason = "auto" | "confirm" | "scroll";

const BannerItem = ({ blok, onDismiss, navigation }: BannerItemPops) => {
  const [isHidden, setIsHidden] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const cookie = getCookie(blok._uid);
  const { id } = useContext(CampsiteContext);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const dismissReason = useRef<BannerDismissReason | null>(null);
  const onNext = navigation?.onNext;

  const shouldRender = isBannerEligible(blok, id);

  const hide = useCallback(() => {
    setIsHidden(true);
  }, []);

  const measureBanner = useCallback((banner: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    bannerRef.current = banner;
    if (!banner) return;

    const updateHeight = () =>
      setProperty("--header-top", `${banner.offsetHeight}px`);
    updateHeight();
    resizeObserverRef.current = new ResizeObserver(updateHeight);
    resizeObserverRef.current.observe(banner);
  }, []);

  useEffect(() => {
    if (isHidden || blok.mode !== "auto" || isHovered || hasFocus) return;

    const timer = setTimeout(() => {
      if (onNext) {
        onNext();
        return;
      }

      dismissReason.current = "auto";
      hide();
    }, 10000);

    return () => clearTimeout(timer);
  }, [blok._uid, blok.mode, hide, isHidden, isHovered, hasFocus, onNext]);

  useEffect(() => {
    if (isHidden || blok.mode !== "auto") return;

    let scrollStart = Math.max(0, window.scrollY);

    const handleScroll = () => {
      const scrollY = Math.max(0, window.scrollY);
      scrollStart = Math.min(scrollStart, scrollY);

      if (scrollY - scrollStart >= 80) {
        dismissReason.current = "scroll";
        hide();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [blok._uid, blok.mode, hide, isHidden]);

  useEffect(() => {
    if (cookie === "true" || !shouldRender) {
      hide();
    } else {
      dismissReason.current = null;
      setIsHidden(false);
    }
  }, [blok._uid, cookie, hide, shouldRender]);

  useEffect(() => {
    // Arrow navigation retains focus; closing a notice removes its controls.
    setHasFocus(bannerRef.current?.contains(document.activeElement) ?? false);
    setIsHovered(bannerRef.current?.matches(":hover") ?? false);
  }, [blok._uid]);

  useEffect(() => () => setProperty("--header-top", "0px"), []);

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dismissReason.current = "confirm";
    setCookie(blok._uid, "true", {});
    hide();
  };

  return (
    <Transition
      appear={true}
      show={!isHidden}
      afterLeave={() => {
        setProperty("--header-top", "0px");
        if (dismissReason.current) onDismiss?.(dismissReason.current);
      }}
      className="absolute w-full"
      enter="transition ease-in duration-300 transform-all"
      enterFrom="opacity-0 -translate-y-full"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease-out duration-300 transform-all"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 -translate-y-full"
    >
      <div
        ref={measureBanner}
        className="relative z-10 bg-brand-primary100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocusCapture={() => setHasFocus(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setHasFocus(false);
        }}
        {...storyblokEditable(blok)}
      >
        <div className="max-w-8xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-white md:px-8">
          <Link
            href={blok.link?.cached_url || "#"}
            className="min-w-0 flex-1 basis-40"
          >
            <div className="flex items-center gap-x-3 md:gap-x-4">
              <div className="w-10 h-10 flex-none rounded-lg bg-brand-primary200 flex items-center justify-center">
                <BellAlertIcon className="w-6 h-6" aria-hidden="true" />
              </div>
              <p
                className="py-2 font-medium break-words duration-150 hover:text-indigo-100"
                aria-live="polite"
                aria-atomic="true"
              >
                {blok?.title}
              </p>
            </div>
          </Link>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {navigation && (
              <div
                className="flex items-center gap-1"
                role="group"
                aria-label="Hinweise durchblättern"
              >
                <button
                  type="button"
                  onClick={navigation.onPrevious}
                  disabled={isHidden}
                  className="p-2 rounded-lg hover:bg-brand-primary50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  aria-label="Vorheriger Hinweis"
                >
                  <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
                </button>
                <span
                  className="min-w-[2.5rem] text-center text-sm tabular-nums"
                  aria-label={`Hinweis ${navigation.index + 1} von ${
                    navigation.count
                  }`}
                >
                  {navigation.index + 1} / {navigation.count}
                </span>
                <button
                  type="button"
                  onClick={navigation.onNext}
                  disabled={isHidden}
                  className="p-2 rounded-lg hover:bg-brand-primary50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  aria-label="Nächster Hinweis"
                >
                  <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            )}
            {blok?.mode === "confirm" && (
              <button
                type="button"
                disabled={isHidden}
                className="p-2 rounded-lg duration-150 hover:bg-brand-primary50 ring-offset-2 focus:ring"
                onClick={handleButtonClick}
                aria-label="Schliessen"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default BannerItem;
